import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, Sparkles, Search } from "lucide-react";
import RenderTips from "@/utils/render-tips";

/**
 * Future tips accordion for /home:
 * - Click Track to expand its races
 * - Click Race to expand its tips (inline)
 *
 * This is intentionally isolated so it doesn't affect other app flows.
 */
export default function FutureTipsAccordion({
  date,
  tracks,
  tipsDisplayLimit = null,
  forceShowAllTips = false,
}) {
  const [openTrackNames, setOpenTrackNames] = useState(() => new Set());
  const [openRaceKeys, setOpenRaceKeys] = useState(() => new Set());
  const [openReviewRaceKeys, setOpenReviewRaceKeys] = useState(() => new Set());

  const safeTracks = useMemo(() => (Array.isArray(tracks) ? tracks : []), [tracks]);

  const getRaceKey = (track, race) =>
    race.key || `future-${date}-${track.name}-${race.number}`;

  const toggleTrack = (track) => {
    const trackName = track?.name;
    if (!trackName) return;

    setOpenTrackNames((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(trackName);
      if (isOpen) {
        next.delete(trackName);
        // When closing a track, also close its races (keeps state tidy)
        setOpenRaceKeys((prevRaces) => {
          const nextRaces = new Set(prevRaces);
          (track.races || []).forEach((race) => nextRaces.delete(getRaceKey(track, race)));
          return nextRaces;
        });
      } else {
        next.add(trackName);
      }
      return next;
    });
  };

  const toggleRace = (raceKey) => {
    setOpenRaceKeys((prev) => {
      const next = new Set(prev);
      if (next.has(raceKey)) next.delete(raceKey);
      else next.add(raceKey);
      return next;
    });
  };

  const toggleRaceReview = (raceKey) => {
    setOpenReviewRaceKeys((prev) => {
      const next = new Set(prev);
      if (next.has(raceKey)) next.delete(raceKey);
      else next.add(raceKey);
      return next;
    });
  };

  const setAllRacesForTrackOpen = (track, shouldOpen) => {
    if (!track?.name) return;
    const raceKeys = (track.races || []).map((race) => getRaceKey(track, race));
    setOpenRaceKeys((prev) => {
      const next = new Set(prev);
      if (shouldOpen) raceKeys.forEach((k) => next.add(k));
      else raceKeys.forEach((k) => next.delete(k));
      return next;
    });
    // Ensure the track is open if we're expanding all
    if (shouldOpen) {
      setOpenTrackNames((prev) => {
        const next = new Set(prev);
        next.add(track.name);
        return next;
      });
    }
  };

  return (
    <div className="future-tips-accordion px-0 pt-2 pb-2 space-y-2">
      <div className="render-tracks-container flex flex-col gap-2 mx-4">
        {safeTracks.map((track) => {
          const isTrackOpen = openTrackNames.has(track.name);
          const trackButtonId = `future-track-${date}-${track.name}`;
          const trackRaceKeys = (track.races || []).map((race) => getRaceKey(track, race));
          const hasRaces = trackRaceKeys.length > 0;
          const allRacesOpen =
            hasRaces && trackRaceKeys.every((k) => openRaceKeys.has(k));

          return (
            <div
              key={`future-${date}-${track.name}`}
              className="track-button-container bg-white rounded-lg shadow mb-0"
            >
              <div className="w-full p-4">
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    className="flex-1 cursor-pointer text-left min-w-0"
                    onClick={() => toggleTrack(track)}
                    aria-expanded={isTrackOpen}
                    aria-controls={`${trackButtonId}-panel`}
                    id={trackButtonId}
                  >
                    <div className="track-name-state-container flex items-center gap-2 min-w-0">
                      <span className="track-name-text font-medium text-[0.9em] truncate">
                        {track.name}
                      </span>
                      {track.state && (
                        <span className="text-xxs text-gray-400 whitespace-nowrap">
                          {" "}
                          ({track.state})
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Keep requested ordering: Expand all / Close all before the chevron */}
                  <button
                    type="button"
                    className="text-xs font-normal text-[#F30099] hover:underline whitespace-nowrap flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAllRacesForTrackOpen(track, !allRacesOpen);
                    }}
                    aria-label={
                      allRacesOpen
                        ? "Close all races for this track"
                        : "Expand all races for this track"
                    }
                  >
                    {allRacesOpen ? "Close all" : "Expand all"}
                  </button>

                  <button
                    type="button"
                    className="flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleTrack(track);
                    }}
                    aria-label={isTrackOpen ? "Collapse track" : "Expand track"}
                    aria-expanded={isTrackOpen}
                    aria-controls={`${trackButtonId}-panel`}
                  >
                    {isTrackOpen ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {isTrackOpen && (
                <div
                  id={`${trackButtonId}-panel`}
                  role="region"
                  aria-labelledby={trackButtonId}
                  className="pb-3"
                >
                  <div className="render-races-container space-y-2">
                    {(track.races || []).map((race) => {
                      const raceKey = getRaceKey(track, race);
                      const isRaceOpen = openRaceKeys.has(raceKey);
                      const isReviewOpen = openReviewRaceKeys.has(raceKey);

                      const resolvedTipsLimit =
                        tipsDisplayLimit === "all"
                          ? race?.data?.firstFourData?.length
                          : tipsDisplayLimit;

                      return (
                        <div key={raceKey} className="mx-2">
                          <button
                            type="button"
                            className={[
                              "race-button-container",
                              "w-full",
                              "bg-gray-50",
                              "rounded-lg",
                              "shadow",
                              "p-4",
                              "cursor-pointer",
                              "text-left",
                              isRaceOpen ? "rounded-b-none" : "",
                            ].join(" ")}
                            onClick={() => toggleRace(raceKey)}
                            aria-expanded={isRaceOpen}
                            aria-controls={`${raceKey}-panel`}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2 flex-wrap min-w-0">
                                <span className="font-medium text-[0.8em] xsm:text-[0.95em] whitespace-nowrap">
                                  Race {race.number}
                                </span>

                                {Array.isArray(race?.data?.firstFourData) &&
                                  race.data.firstFourData.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap min-w-0">
                                      {race.data.firstFourData
                                        .slice(0, 4)
                                        .map((entry, idx, arr) => {
                                          const num =
                                            entry?.number ??
                                            entry?.horseNumber ??
                                            (typeof entry?.formattedName === "string"
                                              ? parseInt(entry.formattedName.split(".")[0], 10)
                                              : null);

                                          if (!num) return null;

                                          const label = `${num}`;

                                          const silkSrc = entry?.horseId
                                            ? `/api/images/silks?filename=${entry.horseId}.jpeg`
                                            : "/images/silk-fallback.jpg";

                                          return (
                                            <span
                                              key={`early-mail-pick-${raceKey}-${num}-${idx}`}
                                              className="inline-flex items-center gap-1 bg-white border border-gray-200 shadow-[1px_1px_1px_0px_rgba(0,0,0,0.12)] rounded-full px-1 xsm:px-2 py-0.5 text-xs xsm:text-sm font-normal xsm:font-semibold text-gray-800 whitespace-nowrap"
                                            >
                                              <span className="silks-container w-4 h-4 flex-shrink-0">
                                                {(entry?.horseId || entry?.silks) && (
                                                  <img
                                                    src={silkSrc}
                                                    alt="Jockey silk"
                                                    className="w-4 h-4 object-contain"
                                                    onError={(e) => {
                                                      if (
                                                        entry?.horseId &&
                                                        entry?.silks &&
                                                        e.target.src.includes("/api/images/silks")
                                                      ) {
                                                        e.target.src = `https://www.puntingform.com.au/images/jockeysilks/${entry.horseId}.jpeg`;
                                                      } else if (
                                                        !e.target.src.includes("silk-fallback.jpg")
                                                      ) {
                                                        e.target.src = "/images/silk-fallback.jpg";
                                                      }
                                                    }}
                                                  />
                                                )}
                                              </span>
                                              <span><span className="hidden text-xxs font-normal">#</span>{label}</span>
                                            </span>
                                          );
                                        })}
                                    </div>
                                  )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {isRaceOpen ? (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                            </div>
                          </button>

                          {isRaceOpen && (
                            <div
                              id={`${raceKey}-panel`}
                              role="region"
                              className="mt-0"
                            >
                              <div className="tips-master-container pt-3 pb-1 px-3 mx-0 mt-0 mb-[2px] rounded-lg rounded-t-none shadow-[0_0_3px_rgba(0,0,0,0.25)] bg-[rgba(255,255,255,0.9)] backdrop-blur-[1.5px] transition-all duration-500">
                                {race?.data?.firstFourData &&
                                Array.isArray(race.data.firstFourData) &&
                                race.data.firstFourData.length > 0 ? (
                                  <RenderTips
                                    externalLeaderboardQueryData={{
                                      [race.key]: race.data,
                                    }}
                                    externalHistoricalData={{}}
                                    externalHasHistoricalData={false}
                                    externalEarlyPredictionsData={{}}
                                    selectedTrack={track.name}
                                    selectedRace={race.number}
                                    today={date}
                                    isChanging={false}
                                    isMounted={true}
                                    isResultsPage={false}
                                    isEarlyMail={true}
                                    showEarlyMailReview={isReviewOpen}
                                    tipsDisplayLimit={resolvedTipsLimit}
                                    forceShowAllTips={forceShowAllTips}
                                  />
                                ) : (
                                  <div className="py-8 text-center">
                                    <AlertTriangle className="mx-auto h-10 w-10 text-yellow-500 mb-3" />
                                    <p className="text-lg font-medium">
                                      No tips data available
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                      The prediction data for this race couldn&apos;t be loaded
                                    </p>
                                  </div>
                                )}
                                <div className="pt-0 pb-1 flex items-center justify-between w-full">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleRaceReview(raceKey);
                                    }}
                                    className="inline-flex items-center cursor-pointer border border-[#fba6e1] px-[8px] py-[6px] rounded-[10px] shadow-[1px_1px_1px_0px_rgba(0,0,0,0.18)] gap-2 text-[10px] font-[800] text-brand-pink uppercase bg-white transform origin-center transition-transform duration-150 ease-out hover:scale-[1.08]"
                                    aria-label={isReviewOpen ? "Hide analysis" : "Read analysis"}
                                  >
                                    <Search className="w-4 h-4 text-brand-pink" aria-hidden="true" />
                                    {isReviewOpen ? "Hide Analysis" : "Read Analysis"}
                                  </button>

                                  <div className="flex items-center">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleRaceReview(raceKey);
                                      }}
                                      className="hidden"
                                      aria-hidden="true"
                                    >
                                      {isReviewOpen ? "Hide" : "Show"} review
                                    </button>

                                    <div className="flex items-center text-[10px] leading-[1.1] text-gray-500 text-right">
                                      <Sparkles
                                        className="w-3 h-3 text-brand-pink mr-1"
                                        aria-hidden="true"
                                      />
                                      <span>
                                        Odds generated by <strong>Lexi</strong><span className="hidden xsm:inline"> - tap to toggle</span>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


